import { useState, useMemo } from "react";
import { openDownloadUrl } from "util/openDownloadUrl";
import classNames from "classnames";
import Card from "components/DesignSystem/Card/Card";
import Table from "components/DesignSystem/Table/V2/Table";
import { Tag, TagColorType } from "components/DesignSystem/Tag/Tag";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { EditPencil } from "components/icons/EditPencil";
import { Cross } from "components/icons/Cross";
import { PlusIcon } from "components/icons/PlusIcon";
import ToolTip from "components/design/toolTip";
import { Spinner } from "components/DesignSystem/Loader/loader";
import { useToast } from "hooks/useToast";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
  flexRender,
} from "react-table-8.10.7";
import { getCommonPinningStyles } from "utils/getCommonPinningStyles";
import { getPinnedShadowClass } from "utils/getPinnedShadowClass";
import { colors } from "constants/colors";
import {
  useSetMonthlyEntryMutation,
  useDeleteMonthlyEntryMutation,
  useGetIncomeStatementUploadQuery,
  useLazyExportMonthlyBreakdownQuery,
} from "store/apis/estimatedTax";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import {
  MonthlyBreakdownEntry,
  ProjectionSource,
  QuarterlyBreakdown,
  SyncConfig,
  TaxAdjustment,
  FinancialAdjustmentMonth,
  IncomeStatementUploadResult,
  TaxWaterfall,
} from "types/Models/estimatedTax";
import { BackendError } from "types/utils/error";
import { PriceInput } from "components/PriceInput/PriceInput";
import Input from "components/DesignSystem/Input/Input";
import { FormikForm } from "components/FormikForm/FormikForm";
import { SyncDataModal } from "./SyncDataModal";
import { UploadedStatementDetailModal } from "./UploadedStatementDetailModal";
import { AdjustmentSlider } from "./AdjustmentSlider";
import { AdjustmentFormModal } from "./AdjustmentFormModal";
import { FinancialAdjustmentFormModal } from "./FinancialAdjustmentFormModal";
import { FinancialAdjustmentSlider } from "./FinancialAdjustmentSlider";
import {
  MONTH_OPTIONS,
  MONTH_SOURCE_FETCH,
  MONTH_SOURCE_UPLOAD,
  MONTH_SOURCE_MANUAL,
  MONTH_SOURCE_PROJECTED,
  ROW_TYPE_NORMAL,
  ROW_TYPE_SECTION_HEADER,
  ROW_TYPE_SUB_HEADER,
  ROW_TYPE_ADJ_ADDBACK,
  ROW_TYPE_ADJ_DEDUCTION,
  ROW_TYPE_SECTION_TOTAL,
  ROW_SIGN_NEGATIVE,
  ROW_SIGN_NEUTRAL,
  BREAKDOWN_ROW_KEY_FINANCIALS_SECTION,
  BREAKDOWN_ROW_KEY_TOTAL_INCOME,
  BREAKDOWN_ROW_KEY_TOTAL_EXPENSES,
  BREAKDOWN_ROW_KEY_FIN_ADJ_SECTION,
  BREAKDOWN_ROW_KEY_TOTAL_NET_INCOME,
  BREAKDOWN_ROW_KEY_ADJ_SECTION,
  BREAKDOWN_ROW_KEY_ADJ_ADDBACKS,
  BREAKDOWN_ROW_KEY_ADJ_DEDUCTIONS,
  BREAKDOWN_ROW_KEY_S174_ADDBACK,
  BREAKDOWN_ROW_KEY_TOTAL_ADJUSTMENT,
  BREAKDOWN_ROW_KEY_TAXABLE_INCOME_SECTION,
  BREAKDOWN_ROW_KEY_PENDING_NOL,
  BREAKDOWN_ROW_KEY_USED_NOL,
  BREAKDOWN_ROW_KEY_TOTAL_TAXABLE_INCOME,
  BREAKDOWN_ROW_KEY_FEDERAL_TAX,
} from "./constants";
import RefreshIcon from "static/images/RefreshIcon.svg";
import { DownloadIconV2 } from "components/icons/DownloadIconV2";
import AcceptedChecks from "components/icons/AcceptedChecks";
import { ArrowDown } from "components/icons/ArrowDown";
import { SmallInfo } from "components/icons/SmallInfo";

const SOURCE_TAG: Record<string, TagColorType> = {
  CLOSED: "green",
  FETCH: "blue",
  UPLOAD: "blue",
  MANUAL: "orange",
  PROJECTED: "gray",
  NONE: "gray",
};

const SOURCE_LABEL: Record<string, string> = {
  CLOSED: "Closed",
  FETCH: "Fetched",
  UPLOAD: "Uploaded",
  MANUAL: "Manually entered",
  PROJECTED: "Projected",
  NONE: "No data",
};

type EditForm = {
  total_income: string;
  total_expenses: string;
  s174_removal: string;
  other_deductions: string;
  note: string;
};

type RowType =
  | typeof ROW_TYPE_NORMAL
  | typeof ROW_TYPE_SECTION_HEADER
  | typeof ROW_TYPE_SUB_HEADER
  | typeof ROW_TYPE_ADJ_ADDBACK
  | typeof ROW_TYPE_ADJ_DEDUCTION
  | typeof ROW_TYPE_SECTION_TOTAL;

type RowSign = typeof ROW_SIGN_NEGATIVE | typeof ROW_SIGN_NEUTRAL;

type BreakdownTableRow = {
  key: string;
  label: string;
  sign?: RowSign;
  values: Record<string, number | null>;
  rowType?: RowType;
  indent?: 1 | 2;
};

type Props = {
  months: MonthlyBreakdownEntry[];
  quarters: QuarterlyBreakdown[];
  entityId: string;
  year: string;
  isAdmin: boolean;
  syncConfig: SyncConfig | null;
  adjustments: TaxAdjustment[];
  isFetching?: boolean;
  projectionSource: ProjectionSource | null;
  waterfall: TaxWaterfall | null;
};

const createColumn = createColumnHelper<BreakdownTableRow>();

const SECTION_TOOLTIPS: Record<string, string> = {
  __adj_section__:
    "Adjustments are changes to revenue or expenses based on tax rules, such as meal limitations, bonuses, depreciation, and other non-recurring items.",
  __fin_adj_section__:
    "One-time adjustments are changes to revenue or expenses that happen only once and won’t recur throughout the year.",
  __taxable_income_section__:
    "Net operating loss adjustments are prior-year losses applied to reduce taxable income in the current year.",
};

function fmtAbs(val: number): string {
  return Math.abs(val).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function fmt(val: number): string {
  return val < 0 ? `-$${fmtAbs(val)}` : `$${fmtAbs(val)}`;
}

function sumRowValues(values: Record<string, number | null>): number | null {
  const numericValues = Object.values(values).filter(
    (v): v is number => typeof v === "number"
  );
  if (numericValues.length === 0) return null;
  return numericValues.reduce((acc, v) => acc + v, 0);
}

export const EstimatedTaxBreakdown = ({
  months,
  quarters,
  entityId,
  year,
  isAdmin,
  syncConfig,
  adjustments,
  isFetching = false,
  projectionSource,
  waterfall,
}: Props) => {
  const { alertToast, successToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();

  const [editingMonth, setEditingMonth] = useState<number | null>(null);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [sliderState, setSliderState] = useState<{
    show: boolean;
    month: number | null;
  }>({ show: false, month: null });

  const [adjFormOpen, setAdjFormOpen] = useState(false);
  const [defaultAdjType, setDefaultAdjType] = useState<
    "ADD_BACK" | "DEDUCTION"
  >("ADD_BACK");
  const [defaultAdjMonth, setDefaultAdjMonth] = useState<number | null>(null);

  const [finAdjForm, setFinAdjForm] = useState<{
    open: boolean;
    month: number | null;
  }>({ open: false, month: null });

  const [finAdjSlider, setFinAdjSlider] = useState<{
    show: boolean;
    month: number | null;
  }>({ show: false, month: null });

  const [adjExpanded, setAdjExpanded] = useState(true);
  const [taxableExpanded, setTaxableExpanded] = useState(true);
  const [statementDetailOpen, setStatementDetailOpen] = useState(false);

  const openAdjForm = (
    type: "ADD_BACK" | "DEDUCTION",
    month: number | null = null
  ) => {
    setDefaultAdjType(type);
    setDefaultAdjMonth(month);
    setAdjFormOpen(true);
  };

  const [setEntry, { isLoading: isSaving }] = useSetMonthlyEntryMutation();
  const [deleteEntry] = useDeleteMonthlyEntryMutation();
  const [exportMonthlyBreakdown, { isLoading: isExporting }] =
    useLazyExportMonthlyBreakdownQuery();
  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();
  const { data: incomeStatementUpload } = useGetIncomeStatementUploadQuery(
    { entityId, taxYear: year },
    { skip: !isAdmin }
  );
  const hasUploadedStatement =
    incomeStatementUpload && "uuid" in incomeStatementUpload;

  const syncedUpToLabel = syncConfig
    ? MONTH_OPTIONS.find((o) => o.value === syncConfig.sync_up_to_month)
        ?.label ?? syncConfig.sync_up_to_month
    : null;

  const latestSyncedSource = months.findLast(
    (m) => m.source === MONTH_SOURCE_UPLOAD || m.source === MONTH_SOURCE_FETCH
  )?.source;

  const openEdit = (m: MonthlyBreakdownEntry) => setEditingMonth(m.month);

  const handleSave = async (values: EditForm) => {
    if (!editingMonth) return;
    try {
      await setEntry({
        entityId,
        payload: {
          year,
          month: editingMonth,
          total_income: Number(values.total_income) || 0,
          total_expenses: Number(values.total_expenses) || 0,
          s174_removal: Number(values.s174_removal) || 0,
          other_deductions: Number(values.other_deductions) || 0,
          note: values.note || undefined,
        },
      }).unwrap();
      successToast({ message: "Month figures saved" });
      setEditingMonth(null);
    } catch (e) {
      alertToast({
        message:
          (e as BackendError)?.data?.error?.message ||
          "Failed to save month figures",
      });
    }
  };

  const handleDelete = async (month: number) => {
    try {
      await deleteEntry({ entityId, year, month }).unwrap();
      successToast({ message: "Manual entry removed" });
    } catch (e) {
      alertToast({ message: "Failed to remove entry" });
    }
  };

  const handleExport = async () => {
    if (!groupId) {
      alertToast({ message: "Something went wrong. Please try again." });
      return;
    }

    try {
      const result = await exportMonthlyBreakdown({ entityId, year }).unwrap();

      if (result.excel_document_id) {
        const { download_url } = await getPreviewUrl({
          groupId,
          fileId: result.excel_document_id,
        }).unwrap();

        openDownloadUrl(download_url);
        successToast({ message: "Monthly breakdown exported successfully" });
      } else {
        alertToast({ message: "Nothing to export" });
      }
    } catch (e) {
      alertToast({ message: "Error exporting monthly breakdown" });
    }
  };

  const editingMonthData = months.find((m) => m.month === editingMonth);

  const editInitialValues: EditForm = {
    total_income:
      editingMonthData?.total_income != null
        ? String(editingMonthData.total_income)
        : "",
    total_expenses:
      editingMonthData?.total_expenses != null
        ? String(editingMonthData.total_expenses)
        : "",
    s174_removal:
      editingMonthData?.s174_removal != null
        ? String(editingMonthData.s174_removal)
        : "",
    other_deductions:
      editingMonthData?.other_deductions != null
        ? String(editingMonthData.other_deductions)
        : "",
    note: "",
  };

  const quarterEndColIds = useMemo(() => {
    const lastMonth = months[months.length - 1];
    return new Set(
      months
        .filter((m) => m.month % 3 === 0 && m !== lastMonth)
        .map((m) => `month-${m.year}-${m.month}`)
    );
  }, [months]);

  const quarterlyFederalTax = useMemo(() => {
    const result: Record<string, number> = {};
    for (const q of quarters) {
      const quarterMonths = months.filter(
        (m) => Math.ceil(m.month / 3) === q.quarter
      );
      if (quarterMonths.length === 0) continue;
      const lastMonth = quarterMonths[quarterMonths.length - 1];
      result[`${lastMonth.year}-${lastMonth.month}`] = q.pre_overpayment_amount;
    }
    return result;
  }, [months, quarters]);

  const hasAddBacks = adjustments.some((a) => a.type === "ADD_BACK");
  const hasDeductions = adjustments.some((a) => a.type === "DEDUCTION");
  const showAdjSection = isAdmin || adjustments.length > 0;

  const tableData = useMemo<BreakdownTableRow[]>(() => {
    const fieldValues = (key: keyof MonthlyBreakdownEntry) =>
      months.reduce((acc, m) => {
        const raw = m[key];
        acc[`${m.year}-${m.month}`] = typeof raw === "number" ? raw : null;
        return acc;
      }, {} as Record<string, number | null>);

    const rows: BreakdownTableRow[] = [];

    // Financials
    rows.push({
      key: BREAKDOWN_ROW_KEY_FINANCIALS_SECTION,
      label: "Financials",
      rowType: ROW_TYPE_SECTION_HEADER,
      values: {},
    });
    rows.push({
      key: BREAKDOWN_ROW_KEY_TOTAL_INCOME,
      label: "Total Income",
      rowType: ROW_TYPE_NORMAL,
      indent: 1,
      values: fieldValues("total_income"),
    });
    rows.push({
      key: BREAKDOWN_ROW_KEY_TOTAL_EXPENSES,
      label: "Total Expenses",
      rowType: ROW_TYPE_NORMAL,
      indent: 1,
      sign: ROW_SIGN_NEGATIVE,
      values: fieldValues("total_expenses"),
    });
    rows.push({
      key: BREAKDOWN_ROW_KEY_FIN_ADJ_SECTION,
      label: "Financial Adjustments",
      rowType: ROW_TYPE_SECTION_HEADER,
      indent: 1,
      values: fieldValues("financial_adjustments"),
    });
    rows.push({
      key: BREAKDOWN_ROW_KEY_TOTAL_NET_INCOME,
      label: "Total Net Income",
      rowType: ROW_TYPE_SECTION_TOTAL,
      values: fieldValues("total_amount"),
    });

    // Adjustments (conditional)
    if (showAdjSection) {
      rows.push({
        key: BREAKDOWN_ROW_KEY_ADJ_SECTION,
        label: "Adjustments",
        rowType: ROW_TYPE_SECTION_HEADER,
        values: {},
      });
      if (adjExpanded) {
        if (hasAddBacks || isAdmin) {
          rows.push({
            key: BREAKDOWN_ROW_KEY_ADJ_ADDBACKS,
            label: "Add-backs",
            rowType: ROW_TYPE_ADJ_ADDBACK,
            indent: 1,
            values: months.reduce((acc, m) => {
              acc[`${m.year}-${m.month}`] = m.add_backs;
              return acc;
            }, {} as Record<string, number | null>),
          });
        }
        if (hasDeductions || isAdmin) {
          rows.push({
            key: BREAKDOWN_ROW_KEY_ADJ_DEDUCTIONS,
            label: "Deductions",
            rowType: ROW_TYPE_ADJ_DEDUCTION,
            indent: 1,
            values: months.reduce((acc, m) => {
              acc[`${m.year}-${m.month}`] = m.deductions;
              return acc;
            }, {} as Record<string, number | null>),
          });
        }
        rows.push({
          key: BREAKDOWN_ROW_KEY_S174_ADDBACK,
          label: "§174 Deductions",
          rowType: ROW_TYPE_NORMAL,
          indent: 1,
          sign: ROW_SIGN_NEGATIVE,
          values: fieldValues("s174_addback"),
        });
        rows.push({
          key: BREAKDOWN_ROW_KEY_TOTAL_ADJUSTMENT,
          label: "Total Adjustment",
          rowType: ROW_TYPE_SECTION_TOTAL,
          values: fieldValues("adjustments"),
        });
      }
    }

    // Taxable Income
    rows.push({
      key: BREAKDOWN_ROW_KEY_TAXABLE_INCOME_SECTION,
      label: "NOL Adjustments",
      rowType: ROW_TYPE_SECTION_HEADER,
      values: {},
    });
    if (taxableExpanded) {
      rows.push({
        key: BREAKDOWN_ROW_KEY_PENDING_NOL,
        label: "Pending NOL",
        rowType: ROW_TYPE_NORMAL,
        indent: 1,
        sign: ROW_SIGN_NEUTRAL,
        values: fieldValues("pending_nol"),
      });
      rows.push({
        key: BREAKDOWN_ROW_KEY_USED_NOL,
        label: "Used NOL",
        rowType: ROW_TYPE_NORMAL,
        indent: 1,
        sign: ROW_SIGN_NEGATIVE,
        values: fieldValues("used_nol"),
      });
    }
    rows.push({
      key: BREAKDOWN_ROW_KEY_TOTAL_TAXABLE_INCOME,
      label: "Total Taxable Income",
      rowType: ROW_TYPE_SECTION_TOTAL,
      values: fieldValues("taxable_income"),
    });

    // Federal Tax
    rows.push({
      key: BREAKDOWN_ROW_KEY_FEDERAL_TAX,
      label: "Federal Tax (21%)",
      rowType: ROW_TYPE_NORMAL,
      values: fieldValues("federal_tax"),
    });

    return rows;
  }, [
    months,
    showAdjSection,
    isAdmin,
    hasAddBacks,
    hasDeductions,
    adjExpanded,
    taxableExpanded,
  ]);

  const sourceLabel =
    projectionSource?.source_type === "closed"
      ? "finalized financials"
      : "financials";
  const projectionTooltip = projectionSource
    ? `Projected from ${projectionSource.from_month}–${projectionSource.to_month} ${projectionSource.year} ${sourceLabel}`
    : "";

  const columns = [
    createColumn.accessor("label", {
      id: "label",
      header: "",
      cell: (info) => {
        const row = info.row.original;
        const label = info.getValue();

        if (row.rowType === ROW_TYPE_SECTION_HEADER) {
          const isAdjSection = row.key === BREAKDOWN_ROW_KEY_ADJ_SECTION;
          const isTaxableSection =
            row.key === BREAKDOWN_ROW_KEY_TAXABLE_INCOME_SECTION;
          const isExpandable = isAdjSection || isTaxableSection;
          const isExpanded = isAdjSection ? adjExpanded : taxableExpanded;
          const toggle = isAdjSection
            ? () => setAdjExpanded((v) => !v)
            : () => setTaxableExpanded((v) => !v);

          return (
            <span
              className={classNames(
                "t-font-semibold t-text-text-60 t-flex t-items-center t-gap-1",
                {
                  "t-pl-4": row.indent === 1,
                  "t-cursor-pointer t-select-none": isExpandable,
                }
              )}
              onClick={isExpandable ? toggle : undefined}
            >
              {isExpandable && (
                <span
                  className={classNames(
                    "t-transition-transform t-duration-200 t-shrink-0 t-flex",
                    {
                      "t-rotate-0": isExpanded,
                      "-t-rotate-90": !isExpanded,
                    }
                  )}
                >
                  <ArrowDown size="16" strokeWidth="1.5" />
                </span>
              )}
              {label}
              {SECTION_TOOLTIPS[row.key] && (
                <ToolTip text={SECTION_TOOLTIPS[row.key]}>
                  <span
                    className="t-text-text-30 t-cursor-default t-flex t-shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <SmallInfo />
                  </span>
                </ToolTip>
              )}
            </span>
          );
        }

        if (row.rowType === ROW_TYPE_SECTION_TOTAL) {
          return (
            <span className="t-font-semibold t-text-text-60">{label}</span>
          );
        }

        if (row.key === BREAKDOWN_ROW_KEY_FEDERAL_TAX) {
          return (
            <span className="t-font-semibold t-text-text-100">{label}</span>
          );
        }

        if (
          row.rowType === ROW_TYPE_ADJ_ADDBACK ||
          row.rowType === ROW_TYPE_ADJ_DEDUCTION
        ) {
          return (
            <span
              className={classNames("t-flex t-items-center t-gap-1", {
                "t-pl-4": row.indent === 1,
              })}
            >
              <span className="t-font-medium t-text-text-60">{label}</span>
            </span>
          );
        }

        return (
          <div
            className={classNames("t-flex t-items-center t-gap-1", {
              "t-pl-4": row.indent === 1,
            })}
          >
            {label}
          </div>
        );
      },
    }),
    ...months.map((m) =>
      createColumn.accessor((row) => row.values[`${m.year}-${m.month}`], {
        id: `month-${m.year}-${m.month}`,
        header: () => {
          return (
            <div className="t-flex t-flex-col t-items-center t-gap-0.5">
              <span>{m.label}</span>
              <div className="t-flex t-items-center t-gap-1">
                <Tag
                  tagType={SOURCE_TAG[m.source] || "gray"}
                  icon={false}
                  rounded
                  size="small"
                >
                  <span className="t-flex t-items-center t-gap-1">
                    {SOURCE_LABEL[m.source] || m.source}
                    {m.source === MONTH_SOURCE_PROJECTED &&
                      projectionSource && (
                        <ToolTip text={projectionTooltip} side="top">
                          <span className="t-cursor-default t-flex t-shrink-0">
                            <SmallInfo />
                          </span>
                        </ToolTip>
                      )}
                  </span>
                </Tag>
                {isAdmin &&
                  (m.source === MONTH_SOURCE_MANUAL ||
                    m.source === MONTH_SOURCE_PROJECTED) && (
                    <ToolTip
                      text={
                        m.source === MONTH_SOURCE_MANUAL
                          ? "Remove manual entry"
                          : "Edit month"
                      }
                    >
                      <Button
                        customType="link"
                        size="small"
                        onClick={() =>
                          m.source === MONTH_SOURCE_MANUAL
                            ? handleDelete(m.month)
                            : openEdit(m)
                        }
                      >
                        <span className="t-text-text-30">
                          {m.source === MONTH_SOURCE_MANUAL ? (
                            <Cross color="currentColor" size="16" />
                          ) : (
                            <EditPencil className="t-text-text-30" />
                          )}
                        </span>
                      </Button>
                    </ToolTip>
                  )}
              </div>
            </div>
          );
        },
        cell: (info) => {
          const val = info.getValue();
          const { sign, rowType, key: rowKey } = info.row.original;

          if (rowKey === BREAKDOWN_ROW_KEY_FEDERAL_TAX) {
            const monthKey = `${m.year}-${m.month}`;
            const quarterAmount = quarterlyFederalTax[monthKey];
            if (quarterAmount === undefined) return null;
            return (
              <span className="t-font-semibold">{fmt(quarterAmount)}</span>
            );
          }

          if (rowType === ROW_TYPE_SUB_HEADER) {
            return <span className="t-pr-2">—</span>;
          }

          if (rowType === ROW_TYPE_SECTION_HEADER) {
            if (rowKey === BREAKDOWN_ROW_KEY_ADJ_SECTION && isAdmin) {
              return (
                <Button
                  customType="ghost_icon"
                  size="small"
                  onClick={() => openAdjForm("ADD_BACK", m.month)}
                >
                  <PlusIcon />
                </Button>
              );
            }
            if (rowKey === BREAKDOWN_ROW_KEY_FIN_ADJ_SECTION) {
              const hasValue =
                val !== null && val !== undefined && (val as number) !== 0;
              if (hasValue) {
                return (
                  <span
                    className="t-cursor-pointer t-font-medium hover:t-underline"
                    onClick={() =>
                      setFinAdjSlider({ show: true, month: m.month })
                    }
                  >
                    {fmt(val as number)}
                  </span>
                );
              }
              if (isAdmin) {
                return (
                  <Button
                    customType="ghost_icon"
                    size="small"
                    onClick={() =>
                      setFinAdjForm({ open: true, month: m.month })
                    }
                  >
                    <PlusIcon />
                  </Button>
                );
              }
              return null;
            }
            return null;
          }

          const isNull = val === null || val === undefined;
          if (isNull) return <span className="t-pr-2">—</span>;

          const numVal = typeof val === "number" ? val : 0;

          if (rowType === ROW_TYPE_ADJ_ADDBACK) {
            return <span>${fmtAbs(numVal)}</span>;
          }

          if (rowType === ROW_TYPE_ADJ_DEDUCTION) {
            return <span>-${fmtAbs(numVal)}</span>;
          }

          if (rowType === ROW_TYPE_SECTION_TOTAL) {
            return (
              <span className="t-font-semibold t-text-text-60">
                {fmt(numVal)}
              </span>
            );
          }

          if (sign === ROW_SIGN_NEGATIVE && numVal !== 0)
            return `-$${fmtAbs(numVal)}`;
          return fmt(numVal);
        },
      })
    ),
    createColumn.accessor((row) => row.values, {
      id: "total",
      header: "Total",
      cell: (info) => {
        const { sign, rowType, key: rowKey, values } = info.row.original;

        if (rowKey === BREAKDOWN_ROW_KEY_FEDERAL_TAX) {
          const totalFederalTax = quarters.reduce(
            (acc, q) => acc + q.pre_overpayment_amount,
            0
          );
          return (
            <span className="t-font-semibold">{fmt(totalFederalTax)}</span>
          );
        }

        if (rowKey === BREAKDOWN_ROW_KEY_PENDING_NOL) {
          return <span className="t-pr-2">—</span>;
        }

        if (rowKey === BREAKDOWN_ROW_KEY_USED_NOL) {
          const nolApplied = waterfall?.nol_applied;
          if (nolApplied === null || nolApplied === undefined) {
            return <span className="t-pr-2">—</span>;
          }
          return `-$${fmtAbs(nolApplied)}`;
        }

        if (rowType === ROW_TYPE_SUB_HEADER) {
          return <span className="t-pr-2">—</span>;
        }

        if (rowType === ROW_TYPE_SECTION_HEADER) {
          return null;
        }

        const total = sumRowValues(values);
        const isNull = total === null;
        if (isNull) return <span className="t-pr-2">—</span>;

        if (rowType === ROW_TYPE_ADJ_ADDBACK) {
          return <span>${fmtAbs(total)}</span>;
        }

        if (rowType === ROW_TYPE_ADJ_DEDUCTION) {
          return <span>-${fmtAbs(total)}</span>;
        }

        if (rowType === ROW_TYPE_SECTION_TOTAL) {
          return (
            <span className="t-font-semibold t-text-text-60">{fmt(total)}</span>
          );
        }

        if (sign === ROW_SIGN_NEGATIVE && total !== 0)
          return `-$${fmtAbs(total)}`;
        return fmt(total);
      },
    }),
  ];

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnPinning: { left: ["label"] },
    },
  });

  return (
    <Card.Root className="t-border-none !t-px-0">
      <Card.Header bordered={false}>
        <div className="t-flex t-items-center t-justify-between t-w-full">
          <div className="t-flex t-flex-col t-gap-0.5">
            <p className="t-text-body-sm t-text-text-30 t-mt-0">
              Closed months use actuals. Projected months carry forward the last
              closed month's figures.
            </p>
          </div>
          <div className="t-flex t-items-center t-gap-1 t-shrink-0">
            {isAdmin && (
              <>
                {hasUploadedStatement &&
                  latestSyncedSource === MONTH_SOURCE_UPLOAD && (
                    <div className="t-flex t-items-center t-gap-3">
                      <div className="t-flex t-items-center t-gap-1.5">
                        <span className="t-size-4 t-shrink-0 t-overflow-hidden t-flex t-items-center t-justify-center">
                          <span className="t-scale-75">
                            <AcceptedChecks color="#12B76A" />
                          </span>
                        </span>
                        <span className="t-text-body-sm t-text-text-30">
                          Synced from upload
                        </span>
                      </div>
                      <div className="t-w-px t-h-4 t-bg-neutral-20" />
                    </div>
                  )}
                {syncConfig && latestSyncedSource === MONTH_SOURCE_FETCH && (
                  <div className="t-flex t-items-center t-gap-3">
                    <div className="t-flex t-items-center t-gap-1.5">
                      <span className="t-size-4 t-shrink-0 t-overflow-hidden t-flex t-items-center t-justify-center">
                        <span className="t-scale-75">
                          <AcceptedChecks color="#12B76A" />
                        </span>
                      </span>
                      <span className="t-text-body-sm t-text-text-30">
                        Synced till{" "}
                        <span className="t-font-medium">{syncedUpToLabel}</span>
                      </span>
                    </div>
                    <div className="t-w-px t-h-4 t-bg-neutral-20" />
                  </div>
                )}
                <Button
                  customType="primary"
                  size="small"
                  onClick={() => setSyncModalOpen(true)}
                >
                  <div className="t-flex t-items-center t-gap-1.5 !t-text-body-sm">
                    <img
                      src={RefreshIcon}
                      alt="RefreshIcon"
                      className="t-size-3 t-brightness-0 t-invert"
                    />
                    Update Financials
                  </div>
                </Button>
              </>
            )}
            <div className="t-flex t-items-center t-gap-0.5">
              <Button
                customType="secondary"
                size="small"
                onClick={handleExport}
                isLoading={isExporting}
                disabled={isExporting}
              >
                <div className="t-flex t-items-center t-gap-1.5">
                  <DownloadIconV2 className="t-size-4" />
                </div>
              </Button>
            </div>
          </div>
        </div>
      </Card.Header>
      <Card.Body className="!t-mt-2">
        {months.length === 0 && (
          <p className="t-text-body-sm t-text-text-30 t-text-center t-py-12">
            No monthly data available yet.
          </p>
        )}
        <div
          className={classNames("t-relative", {
            "t-hidden": months.length === 0,
          })}
        >
          {isFetching && (
            <div className="t-absolute t-inset-0 t-z-20 t-flex t-items-center t-justify-center t-bg-white/60 t-rounded">
              <Spinner size="small" />
            </div>
          )}
          <Table.Container
            sticky={false}
            className="t-w-full t-overflow-x-auto t-pb-4"
          >
            <Table.Content className="t-text-body-sm t-border-separate t-border-spacing-0 t-min-w-[900px]">
              <Table.Head>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Table.HeadRow key={headerGroup.id}>
                    {headerGroup.headers.map((header, idx) => {
                      const pinStyles = getCommonPinningStyles(
                        header.column,
                        colors.surface["lighter-grey"],
                        "transparent",
                        2
                      );
                      const { boxShadow: pinnedShadow } = getPinnedShadowClass(
                        header.column
                      );
                      return (
                        <Table.HeadCell
                          key={header.id}
                          isUpperCase={false}
                          style={pinStyles}
                          className={classNames(pinnedShadow, {
                            "t-w-56 t-whitespace-nowrap t-overflow-hidden t-max-w-[224px]":
                              idx === 0,
                            "t-text-right t-min-w-[90px] t-border-r t-border-neutral-10":
                              idx !== 0,
                            "!t-border-r-2 !t-border-neutral-20":
                              quarterEndColIds.has(header.id),
                          })}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </Table.HeadCell>
                      );
                    })}
                  </Table.HeadRow>
                ))}
              </Table.Head>
              <Table.Body>
                {table.getRowModel().rows.map((row) => {
                  const { key: rowKey, rowType } = row.original;
                  const isSectionHeader = rowType === ROW_TYPE_SECTION_HEADER;
                  const isSectionTotal = rowType === ROW_TYPE_SECTION_TOTAL;
                  const isFederalTax = rowKey === BREAKDOWN_ROW_KEY_FEDERAL_TAX;
                  const isAdjRow =
                    rowType === ROW_TYPE_ADJ_ADDBACK ||
                    rowType === ROW_TYPE_ADJ_DEDUCTION;

                  const rowClassName = classNames({
                    "t-bg-surface-lighter-grey":
                      isSectionTotal || isFederalTax || isSectionHeader,
                    "t-bg-white":
                      !isSectionTotal && !isFederalTax && !isSectionHeader,
                  });

                  return (
                    <Table.Row key={row.id} className={rowClassName}>
                      {row.getVisibleCells().map((cell, cellIdx) => {
                        const isValueCell = cellIdx !== 0;
                        const isClickableCell = isAdjRow && isValueCell;
                        const colId = cell.column.id;
                        const isFederalTaxHiddenCell =
                          isFederalTax &&
                          isValueCell &&
                          quarterlyFederalTax[colId.replace("month-", "")] ===
                            undefined;
                        const monthNum = isClickableCell
                          ? parseInt(
                              colId.split("-")[colId.split("-").length - 1]
                            )
                          : null;

                        const isGreyRow =
                          isSectionHeader || isSectionTotal || isFederalTax;
                        const rowBgColor = isGreyRow
                          ? colors.surface["lighter-grey"]
                          : colors.surface.DEFAULT;
                        const pinStyles = getCommonPinningStyles(
                          cell.column,
                          rowBgColor
                        );
                        const { boxShadow: pinnedShadow } =
                          getPinnedShadowClass(cell.column);

                        return (
                          <Table.Cell
                            key={cell.id}
                            style={pinStyles}
                            onClick={
                              isClickableCell
                                ? () =>
                                    setSliderState({
                                      show: true,
                                      month: monthNum,
                                    })
                                : undefined
                            }
                            className={classNames(pinnedShadow, {
                              "t-font-medium t-text-text-60 t-whitespace-nowrap t-overflow-hidden t-w-56 t-max-w-[224px] t-border-r t-border-neutral-10":
                                cellIdx === 0,
                              "t-text-right t-tabular-nums t-text-text-100":
                                cellIdx !== 0,
                              "t-border-r t-border-neutral-10":
                                cellIdx !== 0 && !isFederalTaxHiddenCell,
                              "!t-border-r-2 !t-border-neutral-20":
                                quarterEndColIds.has(cell.column.id),
                              "t-cursor-pointer hover:t-bg-surface-lighter-grey/70":
                                isClickableCell,
                              "!t-border-t !t-border-t-neutral-20":
                                isFederalTax,
                            })}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </Table.Cell>
                        );
                      })}
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table.Content>
          </Table.Container>
        </div>
      </Card.Body>

      <SyncDataModal
        open={syncModalOpen}
        onClose={() => setSyncModalOpen(false)}
        entityId={entityId}
        year={year}
        syncConfig={syncConfig}
        months={months}
      />

      {hasUploadedStatement && statementDetailOpen && (
        <UploadedStatementDetailModal
          open={statementDetailOpen}
          onClose={() => setStatementDetailOpen(false)}
          upload={incomeStatementUpload as IncomeStatementUploadResult}
          onReupload={() => {
            setStatementDetailOpen(false);
            setSyncModalOpen(true);
          }}
        />
      )}

      {/* Month edit modal */}
      <Modal.Root
        open={editingMonth !== null && isAdmin}
        onOpenChange={(open) => !open && setEditingMonth(null)}
      >
        <Modal.Content>
          <FormikForm<EditForm>
            key={editingMonth}
            initialValues={editInitialValues}
            onSubmit={handleSave}
          >
            <Modal.Header>
              <Modal.Title>Edit {editingMonthData?.label}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="t-space-y-4">
              <div className="t-grid t-grid-cols-2 t-gap-4">
                <PriceInput label="Total Income" name="total_income" />
                <PriceInput label="Total Expenses" name="total_expenses" />
              </div>
              <Input
                withForm
                name="note"
                label="Note"
                placeholder="Optional note"
              />
            </Modal.Body>
            <Modal.FooterButtonGroup>
              <Modal.RawClose asChild>
                <Button disabled={isSaving}>Cancel</Button>
              </Modal.RawClose>
              <Button
                type="submit"
                customType="primary"
                isLoading={isSaving}
                disabled={isSaving}
              >
                Save
              </Button>
            </Modal.FooterButtonGroup>
          </FormikForm>
        </Modal.Content>
      </Modal.Root>

      <AdjustmentSlider
        show={sliderState.show}
        onClose={() => setSliderState({ show: false, month: null })}
        entityId={entityId}
        taxYear={year}
        selectedMonth={sliderState.month}
        isAdmin={isAdmin}
      />

      <AdjustmentFormModal
        open={adjFormOpen}
        onClose={() => setAdjFormOpen(false)}
        entityId={entityId}
        taxYear={year}
        adjustment={null}
        defaultType={defaultAdjType}
        defaultMonth={defaultAdjMonth}
      />

      {finAdjForm.month != null && (
        <FinancialAdjustmentFormModal
          open={finAdjForm.open}
          onClose={() => setFinAdjForm({ open: false, month: null })}
          entityId={entityId}
          year={year}
          monthCode={
            MONTH_OPTIONS[finAdjForm.month - 1]
              .value as FinancialAdjustmentMonth
          }
          monthLabel={MONTH_OPTIONS[finAdjForm.month - 1].label}
          adjustment={null}
          defaultType="INCOME"
        />
      )}

      <FinancialAdjustmentSlider
        show={finAdjSlider.show}
        onClose={() => setFinAdjSlider({ show: false, month: null })}
        entityId={entityId}
        year={year}
        month={finAdjSlider.month}
        defaultType="INCOME"
        isAdmin={isAdmin}
      />
    </Card.Root>
  );
};
